import React, { useState, useEffect } from 'react';
import { ChevronLeft, Edit, Trash2, Save, X, Package, User, Shield, Mail, Phone, Eye, EyeOff } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import fetchData from '../../api/fetchApi';

const UserProfilePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [user, setUser] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [accountStatus, setAccountStatus] = useState(true);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const data = await fetchData(`admin/client?uuid=${id}`);
        setUser(data.client);
        setSubscriptions(data.subscriptions || []);
        setFullName(data.client.name || "");
        setEmail(data.client.apple_email || "");
        setPhone(data.client.phone || "");
        setAccountStatus(data.client.package_status === 'active');
      } catch (error) {
        console.error("Failed to fetch user data:", error);
      }
    };
    fetchUser();
  }, [id]);

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="p-4 md:p-6 min-h-screen" dir="rtl">
      <div className="mx-auto">
        <div className="flex items-center mb-6 gap-3">
            <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-200 transition-colors">
              <ChevronLeft size={24} className="text-gray-600 transform rotate-180" />
            </button>
            <h1 className="text-xl md:text-2xl font-bold text-[#1D2035]">
              ملف المستخدم
              </h1>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 grid grid-cols-1 lg:grid-cols-3 gap-x-12 gap-y-6">
            {/* Left Column */}
            <div className="space-y-6">
              <h3 className="font-semibold text-lg text-gray-800">بيانات المستخدم</h3>
              <div className="flex flex-col items-center gap-4">
                <img src={user.profile_pic || `https://ui-avatars.com/api/?background=random&name=${encodeURIComponent(user.name)}&format=svg`} alt={user.name} className="w-24 h-24 rounded-full" />
                <div className="text-center">
                  <div className="font-semibold text-gray-800">{user.name}</div>
                  <div className="text-sm text-gray-500">{user.apple_email || '—'}</div>
                  <div className="text-sm text-gray-500">{user.phone || '—'}</div>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    <span>حالة الحساب : </span>
                    <span className={accountStatus ? "text-green-600" : "text-red-600"}>{accountStatus ? 'يعمل' : 'متوقف'}</span>
                  </div>
                  <label className="inline-flex items-center cursor-pointer">
                      <input type="checkbox" checked={accountStatus} onChange={()=>setAccountStatus(!accountStatus)} className="sr-only peer" />
                      <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                  </label>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-semibold mb-2">الاشتراكات</h4>
                  {subscriptions.length === 0 && <div className="text-gray-500 text-sm">لا يوجد اشتراكات</div>}
                  {subscriptions.map((sub, idx) => (
                    <div key={idx} className="mb-2 p-2 rounded bg-gray-50 border border-gray-100">
                      <div className="text-sm text-gray-700">{sub.package_name}</div>
                      <div className="text-xs text-gray-500">{sub.status} | {sub.start_date} - {sub.end_date}</div>
                      {sub.package_payment && (
                        <div className="text-xs text-gray-500 mt-1">طريقة الدفع: {sub.package_payment.method} | المبلغ: {sub.package_payment.amount} | حالة الدفع: {sub.package_payment.status}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Right Column */}
            <div className="lg:col-span-2">
              <h3 className="text-lg font-semibold mb-4 text-gray-800">معلومات الاتصال</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">الاسم الكامل</label>
                  <input value={fullName} onChange={(e)=>setFullName(e.target.value)} className="w-full rounded-lg border border-gray-300 p-3 bg-white text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">البريد الإلكتروني</label>
                  <input value={email} onChange={(e)=>setEmail(e.target.value)} className="w-full rounded-lg border border-gray-300 p-3 bg-white text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">رقم الهاتف</label>
                  <input value={phone} onChange={(e)=>setPhone(e.target.value)} className="w-full rounded-lg border border-gray-300 p-3 bg-white text-sm" />
                </div>
              </div>

              <h3 className="text-lg font-semibold mt-8 mb-4 text-gray-800">معلومات تسجيل الدخول</h3>
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                {/* No login methods in API, so skip this table or add if available */}
              </div>

              <div className="mt-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">كلمة السر</label>
                  <div className="relative">
                    <input type={showPassword ? "text" : "password"} value={password} onChange={(e)=>setPassword(e.target.value)} placeholder="********" className="w-full rounded-lg border border-gray-300 p-3 bg-white text-sm" />
                    <button onClick={()=>setShowPassword(!showPassword)} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">تأكيد كلمة السر</label>
                  <input type={showPassword ? "text" : "password"} value={passwordConfirm} onChange={(e)=>setPasswordConfirm(e.target.value)} placeholder="********" className="w-full rounded-lg border border-gray-300 p-3 bg-white text-sm" />
                </div>
              </div>

              <div className="flex items-center justify-between mt-8">
                <button className="px-5 py-2.5 rounded-lg border border-red-200 text-red-600 bg-white hover:bg-red-50 font-semibold text-sm">إزالة الحساب</button>
                <div className="flex gap-3">
                  <button onClick={() => navigate(-1)} className="px-5 py-2.5 rounded-lg border bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold text-sm">إلغاء</button>
                  <button
                    className="px-5 py-2.5 rounded-lg bg-[#8C1C13] hover:bg-[#7a1911] text-white font-semibold text-sm flex items-center gap-2"
                    disabled={editLoading}
                    onClick={async () => {
                      setEditLoading(true);
                      setEditError("");
                      try {
                        await fetchData(`admin/client?uuid=${user.uuid}`, 'PATCH', {
                          name: fullName,
                          apple_email: email,
                          phone: phone,
                          package_status: accountStatus ? 'active' : 'inactive',
                          password: password || undefined,
                          password_confirmation: passwordConfirm || undefined
                        });
                        setEditError("تم حفظ التغييرات بنجاح");
                      } catch (e) {
                        setEditError("فشل حفظ التغييرات");
                      } finally {
                        setEditLoading(false);
                      }
                    }}
                  >
                    <Save size={16} />
                    {editLoading ? 'جاري الحفظ...' : 'حفظ التغييرات'}
                  </button>
                </div>
              </div>
              {editError && <div className={`mt-4 text-center text-sm ${editError.includes('نجاح') ? 'text-green-600' : 'text-red-600'}`}>{editError}</div>}
            </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfilePage;
